import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Search, SlidersHorizontal, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    brand: searchParams.get("brand") || "",
    size: searchParams.get("size") || "",
    priceRange: [0, 1000],
  });

  useEffect(() => {
    fetchProducts();
  }, [category, searchParams]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (filters.search) params.set("search", filters.search);
      if (filters.brand) params.set("brand", filters.brand);
      if (filters.size) params.set("size", filters.size);
      if (filters.priceRange[0] > 0) params.set("min_price", filters.priceRange[0]);
      if (filters.priceRange[1] < 1000) params.set("max_price", filters.priceRange[1]);

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ search: filters.search });
    fetchProducts();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.brand) params.set("brand", filters.brand);
    if (filters.size) params.set("size", filters.size);
    setSearchParams(params);
    setFilterOpen(false);
    fetchProducts();
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      brand: "",
      size: "",
      priceRange: [0, 1000],
    });
    setSearchParams({});
  };

  const getCategoryTitle = () => {
    if (category === "mens") return "Men's T-Shirts - U & Mi";
    if (category === "boys") return "Boys T-Shirts - KiTees";
    return "All Products";
  };

  const sizes = category === "boys"
    ? ["4-5Y", "6-7Y", "8-9Y", "10-11Y", "12-13Y"]
    : ["S", "M", "L", "XL", "XXL"];

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium mb-3">Brand</h4>
        <Select
          value={filters.brand}
          onValueChange={(v) => handleFilterChange("brand", v)}
        >
          <SelectTrigger data-testid="brand-filter">
            <SelectValue placeholder="All Brands" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            <SelectItem value="U & Mi">U & Mi (Men's)</SelectItem>
            <SelectItem value="KiTees">KiTees (Boys)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <h4 className="font-medium mb-3">Size</h4>
        <Select
          value={filters.size}
          onValueChange={(v) => handleFilterChange("size", v)}
        >
          <SelectTrigger data-testid="size-filter">
            <SelectValue placeholder="All Sizes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sizes</SelectItem>
            {sizes.map((size) => (
              <SelectItem key={size} value={size}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <h4 className="font-medium mb-3">
          Price Range: ₹{filters.priceRange[0]} - ₹{filters.priceRange[1]}
        </h4>
        <Slider
          value={filters.priceRange}
          onValueChange={(v) => handleFilterChange("priceRange", v)}
          max={1000}
          step={50}
          className="mt-2"
          data-testid="price-filter"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          onClick={applyFilters}
          className="flex-1 bg-primary hover:bg-primary/90 rounded-full"
          data-testid="apply-filters-btn"
        >
          Apply Filters
        </Button>
        <Button
          onClick={clearFilters}
          variant="outline"
          className="rounded-full"
          data-testid="clear-filters-btn"
        >
          Clear
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cotton-100">
      {/* Header */}
      <div className="bg-primary py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl md:text-5xl text-white font-bold" data-testid="products-title">
            {getCategoryTitle()}
          </h1>
          <p className="text-white/80 mt-2">
            {products.length} products available
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="pl-10 rounded-full"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="bg-primary hover:bg-primary/90 rounded-full" data-testid="search-btn">
              Search
            </Button>
          </form>

          {/* Mobile Filter */}
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild className="sm:hidden">
              <Button variant="outline" className="rounded-full" data-testid="mobile-filter-btn">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <h3 className="font-heading text-xl font-bold mb-6">Filters</h3>
              <FilterContent />
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden sm:block w-64 flex-shrink-0">
            <div className="bg-white p-6 rounded-lg shadow-soft sticky top-24">
              <h3 className="font-heading text-xl font-bold mb-6">Filters</h3>
              <FilterContent />
            </div>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[3/4] bg-gray-200 rounded-lg mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">No products found</p>
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="mt-4 rounded-full"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12" data-testid="products-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
